import { useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Kanban,
  List,
  ThumbsUp,
  ThumbsDown,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  OpportunitiesFilters,
  OpportunitiesStats,
  OpportunitiesTable,
} from '../components/opportunities';
import { KanbanBoard } from '../components/opportunities/KanbanBoard';
import {
  useOpportunities,
  useOpportunityStats,
  useMakeDecision,
  useDeleteOpportunity,
} from '../hooks/useOpportunities';
import type { OpportunityListFilters } from '../types/internal-opportunity.types';

type ViewMode = 'list' | 'kanban';

const PAGE_SIZES = [10, 20, 50];

export function OpportunitiesPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [filters, setFilters] = useState<OpportunityListFilters>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  const { data, isLoading, error } = useOpportunities(filters, {
    page,
    pageSize,
    sortBy,
    sortOrder,
  });

  const { data: stats, isLoading: statsLoading } = useOpportunityStats();
  const makeDecision = useMakeDecision();
  const deleteOpportunity = useDeleteOpportunity();

  const handleFiltersChange = (newFilters: OpportunityListFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleDecision = (id: number, decision: 'go' | 'no_go') => {
    makeDecision.mutate(
      { id, decision },
      {
        onSuccess: () => {
          toast.success(
            `Décision ${decision === 'go' ? 'GO' : 'NO-GO'} enregistrée`
          );
        },
        onError: () => {
          toast.error('Erreur lors de l\'enregistrement de la décision');
        },
      }
    );
  };

  const handleDelete = () => {
    if (!deleteId) return;

    deleteOpportunity.mutate(deleteId, {
      onSuccess: () => {
        toast.success('Opportunité supprimée');
        setDeleteId(null);
        setSelectedIds((prev) => prev.filter((id) => id !== deleteId));
      },
      onError: () => {
        toast.error('Erreur lors de la suppression');
      },
    });
  };

  const handleBulkDecision = (decision: 'go' | 'no_go') => {
    if (selectedIds.length === 0) return;

    const promises = selectedIds.map((id) =>
      makeDecision.mutateAsync({ id, decision })
    );

    Promise.all(promises)
      .then(() => {
        toast.success(
          `Décision ${decision === 'go' ? 'GO' : 'NO-GO'} enregistrée pour ${selectedIds.length} opportunité(s)`
        );
        setSelectedIds([]);
      })
      .catch(() => {
        toast.error('Erreur lors de l\'enregistrement des décisions');
      });
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;

    const promises = selectedIds.map((id) => deleteOpportunity.mutateAsync(id));

    Promise.all(promises)
      .then(() => {
        toast.success(`${selectedIds.length} opportunité(s) supprimée(s)`);
        setSelectedIds([]);
        setBulkDeleteConfirm(false);
      })
      .catch(() => {
        toast.error('Erreur lors de la suppression');
        setBulkDeleteConfirm(false);
      });
  };

  const totalPages = data?.meta.totalPages || 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Opportunités</h1>
          <p className="text-muted-foreground">
            Gérez vos appels d'offres importés
          </p>
        </div>
        <div className="flex items-center gap-1 border rounded-lg p-1">
          <Button
            variant={viewMode === 'kanban' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('kanban')}
            className="gap-2"
          >
            <Kanban className="h-4 w-4" />
            Kanban
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="gap-2"
          >
            <List className="h-4 w-4" />
            Liste
          </Button>
        </div>
      </div>

      {/* Stats */}
      <OpportunitiesStats stats={stats} isLoading={statsLoading} />

      {/* Filtres */}
      <OpportunitiesFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        hideStatusFilters={viewMode === 'kanban'}
      />

      {/* Erreur */}
      {error && viewMode === 'list' && (
        <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
          Une erreur est survenue lors du chargement des opportunités.
        </div>
      )}

      {/* Vue Kanban */}
      {viewMode === 'kanban' && <KanbanBoard />}

      {/* Vue Liste */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {/* Barre d'actions groupées */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3 p-3 bg-copper/10 border border-copper/30 rounded-lg">
              <span className="text-sm font-medium">
                {selectedIds.length} sélectionnée(s)
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2 text-green-600 border-green-600 hover:bg-green-50"
                  onClick={() => handleBulkDecision('go')}
                >
                  <ThumbsUp className="h-4 w-4" />
                  GO
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2 text-red-600 border-red-600 hover:bg-red-50"
                  onClick={() => handleBulkDecision('no_go')}
                >
                  <ThumbsDown className="h-4 w-4" />
                  NO-GO
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2 text-destructive border-destructive hover:bg-destructive/10"
                  onClick={() => setBulkDeleteConfirm(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedIds([])}
                >
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {/* Header résultats */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {data?.meta.total
                ? `${data.meta.total} opportunité${data.meta.total > 1 ? 's' : ''} trouvée${data.meta.total > 1 ? 's' : ''}`
                : 'Aucune opportunité'}
            </p>

            <div className="flex items-center gap-4">
              <Select
                value={sortBy}
                onValueChange={(value) => {
                  setSortBy(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Trier par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status">Statut</SelectItem>
                  <SelectItem value="submissionDeadline">Date limite</SelectItem>
                  <SelectItem value="createdAt">Date d'import</SelectItem>
                  <SelectItem value="title">Titre</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={sortOrder}
                onValueChange={(value: 'asc' | 'desc') => {
                  setSortOrder(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Croissant</SelectItem>
                  <SelectItem value="desc">Décroissant</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tableau */}
          <OpportunitiesTable
            opportunities={data?.data || []}
            isLoading={isLoading}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onDecision={handleDecision}
            onDelete={(id: number) => setDeleteId(id)}
          />

          {/* Pagination */}
          {data && data.meta.total > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Afficher
                </span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">
                  par page
                </span>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                <span className="px-4 text-sm">
                  Page {page} sur {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dialog de suppression individuelle */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'opportunité ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'opportunité sera définitivement
              supprimée de votre liste.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de suppression groupée */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selectedIds.length} opportunité(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les opportunités sélectionnées seront
              définitivement supprimées de votre liste.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer {selectedIds.length} opportunité(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
